/**
 * This tab's own MSAL session (MDRS-556: the cache is tab-scoped, in
 * sessionStorage — see authConfig.ts).
 */
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  EventType,
  type IPublicClientApplication,
  InteractionRequiredAuthError,
  InteractionType,
} from "@azure/msal-browser";

import { loginRequest, msalConfig } from "../authConfig";
import {
  getLastSignInMethod,
  hasTabSessionEnded,
  markSignInMethod,
  markTabSessionStarted,
} from "./cookies";

// sessionStorage. Own key, so other sign-ins still go to the default route.
export const FALLBACK_TARGET_KEY = "medirect.sso-fallback-target";

// sessionStorage. Cooldown that stops a redirect loop.
const REDIRECT_FALLBACK_AT_KEY = "medirect.sso-fallback-at";
export const REDIRECT_FALLBACK_COOLDOWN_MS = 2 * 60 * 1000;

let redirectFallbackInFlight = false;

/** Path of the page MSAL returns a sign-in to (VITE_MSAL_REDIRECT_URI can override it). */
const redirectPathname = (): string => {
  try {
    return new URL(msalConfig.auth.redirectUri, window.location.origin).pathname;
  } catch {
    return "/auth-callback";
  }
};

let ssoSilentInFlight: Promise<void> | null = null;

/**
 * ssoSilent(), shared while pending: index.jsx and the route guard both call
 * it on a new tab, and each call opens its own iframe.
 */
export const ssoSilentOnce = (instance: IPublicClientApplication): Promise<void> => {
  if (!ssoSilentInFlight) {
    ssoSilentInFlight = instance
      .ssoSilent({ scopes: loginRequest.scopes })
      .then(() => undefined)
      .finally(() => {
        ssoSilentInFlight = null;
      });
  }
  return ssoSilentInFlight;
};

/**
 * Drops every MSAL account and token this tab holds. This emits no MSAL
 * event, so msal-react keeps its old `accounts` until the page reloads.
 */
export const clearThisTabsMsalCache = async (instance: IPublicClientApplication) => {
  try {
    await instance.clearCache();
  } catch (error) {
    console.error("Clearing this tab's MSAL cache failed:", error);
  }
  instance.setActiveAccount(null);
};

/** On each new sign-in (not a silent refresh): stamp it and record the method. */
export const trackTabSessionStart = (instance: IPublicClientApplication): void => {
  instance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS ||
      (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS &&
        event.interactionType !== InteractionType.Silent)
    ) {
      markTabSessionStarted();
      markSignInMethod("microsoft");
      clearRedirectFallback();
    }
  });
};

export const hasRecentRedirectFallback = (): boolean => {
  try {
    const at = Number(sessionStorage.getItem(REDIRECT_FALLBACK_AT_KEY));
    return !!at && Date.now() - at < REDIRECT_FALLBACK_COOLDOWN_MS;
  } catch {
    // Can't rule out a loop.
    return true;
  }
};

const clearRedirectFallback = (): void => {
  try {
    sessionStorage.removeItem(REDIRECT_FALLBACK_AT_KEY);
  } catch {
    /* sessionStorage unavailable */
  }
};

/** The caller has already ruled out an explicit logout. */
export const shouldRedirectAfterSilentFailure = (error: unknown): boolean => {
  if (getLastSignInMethod() !== "microsoft") return false;
  if (hasRecentRedirectFallback()) return false;
  if (error instanceof InteractionRequiredAuthError) return true;
  return (
    error instanceof BrowserAuthError &&
    error.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout
  );
};

/**
 * Sends the page to Microsoft's sign-in. Resolves only if it didn't leave:
 * storage blocked (so the loop guard can't work), MSAL refused (e.g. another
 * interaction in progress), or MSAL's navigation timeout passed.
 */
export const startRedirectFallback = async (
  instance: IPublicClientApplication,
  targetPath: string
): Promise<void> => {
  try {
    sessionStorage.setItem(REDIRECT_FALLBACK_AT_KEY, String(Date.now()));
    sessionStorage.setItem(FALLBACK_TARGET_KEY, targetPath);
  } catch {
    return;
  }
  // Back from Microsoft's page can restore this page from the bfcache mid-redirect.
  const onRestore = (event: PageTransitionEvent) => {
    if (event.persisted) window.location.reload();
  };
  window.addEventListener("pageshow", onRestore);
  redirectFallbackInFlight = true;
  try {
    await instance.loginRedirect(loginRequest);
  } catch (error) {
    console.error("Redirect sign-in fallback failed:", error);
  }
  window.removeEventListener("pageshow", onRestore);
  redirectFallbackInFlight = false;
};

/** Whether this page is already leaving for Microsoft's sign-in. */
export const isRedirectFallbackInFlight = (): boolean => redirectFallbackInFlight;

/**
 * Same-origin paths only: `navigate({ href })` follows "//host" off-site, and
 * browsers normalise tabs and backslashes, so check the parsed URL.
 */
export const consumeFallbackTarget = (): string | null => {
  let target: string | null;
  try {
    target = sessionStorage.getItem(FALLBACK_TARGET_KEY);
    sessionStorage.removeItem(FALLBACK_TARGET_KEY);
  } catch {
    return null;
  }
  if (!target?.startsWith("/")) return null;
  try {
    const url = new URL(target, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname === redirectPathname()) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
};

/**
 * On page load, before msal-react reads the cache: drop an account a logout
 * has since ended (a reopened or duplicated tab). Skipped on the redirect
 * page, where the cache holds the sign-in that's completing. Never throws:
 * index.jsx awaits it before the first render.
 */
export const dropEndedTabSession = async (
  instance: IPublicClientApplication
): Promise<void> => {
  try {
    if (window.location.pathname === redirectPathname()) return;
    if (instance.getAllAccounts().length === 0 || !hasTabSessionEnded()) return;
    await clearThisTabsMsalCache(instance);
  } catch (error) {
    // useProtectedRoute still rejects the account on its own check.
    console.error("Dropping an ended MSAL session failed:", error);
  }
};
